import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { FormControl } from "react-bootstrap";
import Button from 'src/components/button';
import styles from "./styles.module.scss";

type RenderType = "inputPlaceholder" | "buttonText";

interface useInputEditorReturn {
  Render: FC<{classnames?: string, inlineStyles?: React.CSSProperties}>;
  Editor: FC<{ classnames?: string, maxLength?: number}>;
  output: string;
  reset: (value?: any) => void;
}

export const useInputEditor = (
  initialValue: string,
  renderType?: RenderType
): useInputEditorReturn => {
  const [value, setValue] = useState(initialValue ?? "");
  const valueRef = useRef(initialValue ?? "");

  useEffect(() => { // sync with external value
    valueRef.current = initialValue ?? "";
    setValue(initialValue ?? "");
  }, [initialValue]);

  const handleChange = useCallback((val: string) => {
    valueRef.current = val;
    setValue(val);
  }, []);

  const renderWrapper = useMemo(() => { // Viewer component
    return function RenderComponent({classnames, inlineStyles}: {classnames?: string, inlineStyles?: React.CSSProperties}) {
      return <Renders value={value} renderType={renderType!} classnames={classnames} inlineStyles={inlineStyles} />;
    };
  }, [value, renderType]);

  const editorWrapper = useMemo(() => { // Editor component
    return function EditorComponent({ classnames, maxLength}: { classnames?: string, maxLength?: number }) {
      return <Editor value={valueRef.current} onChange={handleChange} classnames={classnames} maxLength={maxLength} />;
    };
  }, [handleChange]);

  const reset = (value?: any): void => { // reset to initial value or provided value
    if(!value){
      valueRef.current = initialValue ?? ""; 
      setValue(initialValue ?? "");
    } else {
      valueRef.current = value; 
      setValue(value); 
    }
  };

  return { Render: renderWrapper, Editor: editorWrapper, output: value, reset };
};

const Renders = ({value, renderType, classnames, inlineStyles}: {value: string, renderType?: RenderType, classnames?: string, inlineStyles?: React.CSSProperties}) => {
  switch (renderType) {
        case "inputPlaceholder":
          return (
            <FormControl
              placeholder={value}
              disabled={true}
              className={cx(styles.input, classnames)}
            />
          );
        case "buttonText":
          return (
            <button
              className={cx(styles.buttonText, classnames)}
              style={inlineStyles}
              >
                <i className="paperflite-tick"></i> 
                <span className={styles.text}>{value}</span>
              </button>
          );
        default:
          return <>{value}</>;
      }
}

const Editor = ({value, onChange, classnames, maxLength}: {value: string, onChange: (val: string) => void, classnames?: string, maxLength?: number}) => {
 return (
   <FormControl
          className={cx(styles.input, classnames)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength ? maxLength : undefined}
    />
 )
}
